// ═══════════════════════════════════════════════════════════════════════════════
// SECTOR SCREENSHOT SHOWCASE — Dashboard Previews for All 42 sectors
// Auto-playing slideshow with watermarks. Shows the world what's inside.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { SECTOR_MARKETING_DATA } from './SectorMarketingData';
import type { SectorMarketingConfig } from './SectorMarketingData';

// ─── Module Categories Per Sector (1,200+ total across platform) ─────
interface ModuleCategory {
  name: string;
  count: number;
  icon: string;
}

function getSectorModules(sectorId: string): { total: number; categories: ModuleCategory[] } {
  const moduleMap: Record<string, { total: number; categories: ModuleCategory[] }> = {
    medical: {
      total: 127,
      categories: [
        { name: 'Drug Intelligence', count: 18, icon: '💊' },
        { name: 'Clinical Trial Tracking', count: 14, icon: '🧪' },
        { name: 'Adverse Event Engine', count: 12, icon: '⚠️' },
        { name: 'Genomic Data Pipeline', count: 16, icon: '🧬' },
        { name: 'HIPAA/TGA Compliance', count: 11, icon: '🏥' },
        { name: 'AI Diagnostics', count: 15, icon: '🤖' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 14, icon: '🔌' },
        { name: 'Report Generation', count: 8, icon: '📊' },
        { name: 'Evolution Engine', count: 10, icon: '🧠' },
      ],
    },
    finance: {
      total: 134,
      categories: [
        { name: 'Market Analytics', count: 19, icon: '📈' },
        { name: 'Fraud Detection', count: 16, icon: '🕵️' },
        { name: 'Regulatory Compliance', count: 14, icon: '📋' },
        { name: 'Risk Scoring Engine', count: 12, icon: '🎯' },
        { name: 'AML/KYC Pipeline', count: 13, icon: '🔍' },
        { name: 'Trading Intelligence', count: 15, icon: '💹' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 18, icon: '🔌' },
        { name: 'Report Generation', count: 8, icon: '📊' },
        { name: 'Evolution Engine', count: 10, icon: '🧠' },
      ],
    },
    legal: {
      total: 118,
      categories: [
        { name: 'Case Law Intelligence', count: 17, icon: '⚖️' },
        { name: 'Legislation Tracker', count: 13, icon: '📜' },
        { name: 'Contract AI Analysis', count: 14, icon: '📝' },
        { name: 'Privilege Guard', count: 9, icon: '🔒' },
        { name: 'Court Analytics', count: 11, icon: '🏛️' },
        { name: 'Correlation Engine', count: 12, icon: '🔗' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 15, icon: '🔌' },
        { name: 'Report Generation', count: 8, icon: '📊' },
        { name: 'Evolution Engine', count: 10, icon: '🧠' },
      ],
    },
    climate: {
      total: 112,
      categories: [
        { name: 'Carbon Tracking', count: 14, icon: '🌍' },
        { name: 'ESG Scoring', count: 12, icon: '📊' },
        { name: 'Weather Intelligence', count: 15, icon: '🌪️' },
        { name: 'Biodiversity Monitor', count: 10, icon: '🌿' },
        { name: 'TCFD Reporting', count: 11, icon: '📋' },
        { name: 'Satellite Pipeline', count: 13, icon: '🛰️' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 12, icon: '🔌' },
        { name: 'Report Generation', count: 8, icon: '📊' },
        { name: 'Evolution Engine', count: 8, icon: '🧠' },
      ],
    },
    defence: {
      total: 141,
      categories: [
        { name: 'Threat Fusion', count: 18, icon: '🎖️' },
        { name: 'SIGINT Processing', count: 16, icon: '📡' },
        { name: 'Supply Chain Intel', count: 13, icon: '🔗' },
        { name: 'Adversary Profiling', count: 14, icon: '👁️' },
        { name: 'Cyber-Kinetic Correlation', count: 12, icon: '⚡' },
        { name: 'Sovereign DISP', count: 15, icon: '🇦🇺' },
        { name: 'Self-Audit & Repair', count: 11, icon: '🔧' },
        { name: 'Secure Comms', count: 17, icon: '🔐' },
        { name: 'Report Generation', count: 12, icon: '📊' },
        { name: 'Evolution Engine', count: 13, icon: '🧠' },
      ],
    },
    energy: {
      total: 108,
      categories: [
        { name: 'Grid Monitoring', count: 14, icon: '⚡' },
        { name: 'SCADA Intelligence', count: 12, icon: '🏭' },
        { name: 'Commodity Analytics', count: 11, icon: '📈' },
        { name: 'Renewable Tracking', count: 10, icon: '☀️' },
        { name: 'Infrastructure Health', count: 13, icon: '🔧' },
        { name: 'Demand Forecasting', count: 12, icon: '📉' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 11, icon: '🔌' },
        { name: 'Report Generation', count: 8, icon: '📊' },
        { name: 'Evolution Engine', count: 8, icon: '🧠' },
      ],
    },
    maritime: {
      total: 104,
      categories: [
        { name: 'Vessel Tracking', count: 15, icon: '🚢' },
        { name: 'Port Intelligence', count: 11, icon: '🏗️' },
        { name: 'AIS Analytics', count: 12, icon: '📡' },
        { name: 'Sanctions Screening', count: 10, icon: '🚫' },
        { name: 'Route Risk Engine', count: 9, icon: '🗺️' },
        { name: 'ISPS Compliance', count: 11, icon: '📋' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 11, icon: '🔌' },
        { name: 'Report Generation', count: 8, icon: '📊' },
        { name: 'Evolution Engine', count: 8, icon: '🧠' },
      ],
    },
    space: {
      total: 98,
      categories: [
        { name: 'Orbital Tracking', count: 14, icon: '🛰️' },
        { name: 'Conjunction Analysis', count: 11, icon: '💥' },
        { name: 'Spectrum Monitor', count: 10, icon: '📡' },
        { name: 'Launch Intelligence', count: 9, icon: '🚀' },
        { name: 'Debris Catalogue', count: 8, icon: '🌑' },
        { name: 'SSA Correlation', count: 12, icon: '🔭' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 10, icon: '🔌' },
        { name: 'Report Generation', count: 7, icon: '📊' },
        { name: 'Evolution Engine', count: 8, icon: '🧠' },
      ],
    },
    agriculture: {
      total: 96,
      categories: [
        { name: 'Crop Health Engine', count: 14, icon: '🌾' },
        { name: 'Pest Intelligence', count: 11, icon: '🐛' },
        { name: 'Weather Pipeline', count: 10, icon: '🌧️' },
        { name: 'Market Forecasting', count: 9, icon: '📈' },
        { name: 'Biosecurity Monitor', count: 10, icon: '🦠' },
        { name: 'Yield Prediction', count: 8, icon: '📊' },
        { name: 'Self-Audit & Repair', count: 9, icon: '🔧' },
        { name: 'API & Integrations', count: 10, icon: '🔌' },
        { name: 'Report Generation', count: 7, icon: '📊' },
        { name: 'Evolution Engine', count: 8, icon: '🧠' },
      ],
    },
    education: {
      total: 91,
      categories: [
        { name: 'Skills Analysis', count: 13, icon: '🎓' },
        { name: 'Curriculum AI', count: 10, icon: '📚' },
        { name: 'Workforce Alignment', count: 9, icon: '👔' },
        { name: 'Research Intelligence', count: 11, icon: '🔬' },
        { name: 'Institution Scoring', count: 8, icon: '🏫' },
        { name: 'Gap Detection', count: 9, icon: '📉' },
        { name: 'Self-Audit & Repair', count: 8, icon: '🔧' },
        { name: 'API & Integrations', count: 9, icon: '🔌' },
        { name: 'Report Generation', count: 7, icon: '📊' },
        { name: 'Evolution Engine', count: 7, icon: '🧠' },
      ],
    },
  };
  return moduleMap[sectorId] || moduleMap['medical'];
}

// Platform-wide module totals — used inline in the header stats strip below

// ─── Dashboard Preview Data Per Sector ─────────────────────────────
interface DashboardPreview {
  sectorId: string;
  title: string;
  subtitle: string;
  stats: { label: string; value: string; trend: string; trendUp: boolean }[];
  alerts: { severity: 'critical' | 'high' | 'medium' | 'low'; text: string }[];
  chartBars: number[]; // 12 values for a bar chart
  tableRows: {
    col1: string;
    col2: string;
    col3: string;
    status: 'active' | 'warning' | 'critical' | 'resolved';
  }[];
  sidebarItems: string[];
  liveFeeds: string[];
}

function getSectorDashboardPreview(config: SectorMarketingConfig): DashboardPreview {
  const dashboards: Record<string, DashboardPreview> = {
    medical: {
      sectorId: 'medical',
      title: 'Medical Intelligence Hub',
      subtitle: 'Real-Time Clinical & Pharma Intelligence',
      stats: [
        { label: 'Drug Interactions', value: '847K', trend: '+12.3%', trendUp: true },
        { label: 'Adverse Events', value: '2.4M', trend: '+8.7%', trendUp: true },
        { label: 'Clinical Trials', value: '15,842', trend: '+3.1%', trendUp: true },
        { label: 'Risk Alerts', value: '127', trend: '-22%', trendUp: false },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'FDA Safety Alert: Drug Interaction — Compound XR-7 + Metformin',
        },
        {
          severity: 'high',
          text: 'Clinical Trial NCT0847291 — Phase III adverse event cluster detected',
        },
        {
          severity: 'medium',
          text: 'TGA Update: Revised labelling requirements for Class IIb devices',
        },
      ],
      chartBars: [65, 78, 82, 71, 88, 94, 87, 92, 96, 89, 91, 97],
      tableRows: [
        {
          col1: 'Pfizer BNT162b3',
          col2: 'Phase III',
          col3: '2,847 participants',
          status: 'active',
        },
        {
          col1: 'Moderna mRNA-4152',
          col2: 'Phase II',
          col3: '1,203 participants',
          status: 'active',
        },
        {
          col1: 'AstraZeneca AZD2816',
          col2: 'Phase III',
          col3: '4,102 participants',
          status: 'warning',
        },
        { col1: 'Johnson & Johnson', col2: 'Phase I', col3: '412 participants', status: 'active' },
      ],
      sidebarItems: [
        'Dashboard',
        'Drug Intelligence',
        'Clinical Trials',
        'Adverse Events',
        'Genomic Data',
        'Compliance',
        'Reports',
        'API Access',
      ],
      liveFeeds: [
        'PubMed',
        'FDA MedWatch',
        'ClinicalTrials.gov',
        'WHO Drug Alerts',
        'TGA Database',
      ],
    },
    finance: {
      sectorId: 'finance',
      title: 'Financial Intelligence Centre',
      subtitle: 'Autonomous Market & Regulatory Intelligence',
      stats: [
        { label: 'Market Signals', value: '1.2M', trend: '+18.4%', trendUp: true },
        { label: 'Regulatory Filings', value: '847K', trend: '+6.2%', trendUp: true },
        { label: 'Fraud Alerts', value: '2,341', trend: '-31%', trendUp: false },
        { label: 'Risk Score', value: '94.2', trend: '+1.8%', trendUp: true },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'APRA CPS 234 Compliance Gap — Authentication controls below threshold',
        },
        {
          severity: 'high',
          text: 'Unusual trading pattern detected — ASX:BHP cross-market correlation',
        },
        { severity: 'medium', text: 'SEC Filing 10-K — Material risk disclosure anomaly (TSLA)' },
      ],
      chartBars: [72, 68, 81, 77, 85, 92, 88, 79, 94, 91, 86, 93],
      tableRows: [
        { col1: 'ASX:CBA', col2: 'Anomaly Score: 87', col3: '$112.40 (+2.3%)', status: 'warning' },
        { col1: 'NYSE:JPM', col2: 'Anomaly Score: 23', col3: '$198.72 (+0.8%)', status: 'active' },
        { col1: 'LSE:HSBA', col2: 'Anomaly Score: 91', col3: '£6.42 (-1.2%)', status: 'critical' },
        { col1: 'ASX:WBC', col2: 'Anomaly Score: 12', col3: '$28.90 (+1.1%)', status: 'active' },
      ],
      sidebarItems: [
        'Dashboard',
        'Market Intel',
        'Regulatory',
        'Fraud Detection',
        'Risk Engine',
        'Compliance',
        'Reports',
        'API Access',
      ],
      liveFeeds: ['ASX', 'NYSE', 'SEC EDGAR', 'APRA', 'Bloomberg'],
    },
    legal: {
      sectorId: 'legal',
      title: 'Legal Intelligence Operations',
      subtitle: 'Autonomous Case Law & Regulatory Tracking',
      stats: [
        { label: 'Court Opinions', value: '2.4M', trend: '+4.2%', trendUp: true },
        { label: 'Regulations', value: '185K', trend: '+7.8%', trendUp: true },
        { label: 'Active Bills', value: '3,241', trend: '+12%', trendUp: true },
        { label: 'Correlations', value: '47K', trend: '+22%', trendUp: true },
      ],
      alerts: [
        {
          severity: 'high',
          text: 'Privacy Act Amendment — New mandatory breach notification requirements',
        },
        {
          severity: 'medium',
          text: 'High Court precedent shift detected — Contract interpretation (AU)',
        },
        { severity: 'low', text: 'US Congressional Bill HR-4872 — Cybersecurity liability update' },
      ],
      chartBars: [54, 67, 72, 68, 81, 76, 84, 89, 82, 91, 87, 94],
      tableRows: [
        {
          col1: 'Smith v Corp Ltd [2026]',
          col2: 'Federal Court',
          col3: 'Privacy Breach',
          status: 'active',
        },
        { col1: 'ACCC v TechCo', col2: 'High Court', col3: 'Consumer Law', status: 'active' },
        {
          col1: 'Privacy Act s26WE',
          col2: 'Parliament',
          col3: 'Amendment Bill',
          status: 'warning',
        },
        {
          col1: 'EU AI Act Art 52',
          col2: 'EU Commission',
          col3: 'AI Regulation',
          status: 'active',
        },
      ],
      sidebarItems: [
        'Dashboard',
        'Case Law',
        'Regulations',
        'Bills Monitor',
        'Contract AI',
        'Privilege Guard',
        'Reports',
        'API Access',
      ],
      liveFeeds: ['Federal Court', 'High Court', 'Parliament', 'EU Commission', 'US Congress'],
    },
    climate: {
      sectorId: 'climate',
      title: 'Climate Intelligence Platform',
      subtitle: 'Real-Time Environmental & ESG Monitoring',
      stats: [
        { label: 'Carbon Records', value: '4.7M', trend: '+9.1%', trendUp: true },
        { label: 'Weather Events', value: '12,841', trend: '+34%', trendUp: true },
        { label: 'ESG Score Avg', value: '72.4', trend: '+3.2%', trendUp: true },
        { label: 'Risk Zones', value: '847', trend: '+18%', trendUp: true },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'Category 5 cyclone forming — Coral Sea, 72hr landfall projection',
        },
        {
          severity: 'high',
          text: 'Carbon offset verification failure — Gold Standard Project #4827',
        },
        { severity: 'medium', text: 'TCFD reporting deadline approaching — 14 days remaining' },
      ],
      chartBars: [42, 48, 55, 61, 58, 67, 72, 78, 84, 89, 91, 95],
      tableRows: [
        { col1: 'BHP Group', col2: 'Scope 1: 14.2Mt', col3: 'ESG: 68/100', status: 'warning' },
        { col1: 'Rio Tinto', col2: 'Scope 1: 11.8Mt', col3: 'ESG: 71/100', status: 'active' },
        {
          col1: 'Woodside Energy',
          col2: 'Scope 1: 8.4Mt',
          col3: 'ESG: 54/100',
          status: 'critical',
        },
        { col1: 'Origin Energy', col2: 'Scope 1: 6.1Mt', col3: 'ESG: 76/100', status: 'active' },
      ],
      sidebarItems: [
        'Dashboard',
        'Carbon Tracking',
        'Weather Intel',
        'ESG Scoring',
        'Risk Maps',
        'TCFD Reports',
        'Biodiversity',
        'API Access',
      ],
      liveFeeds: ['NASA GISS', 'NOAA', 'ESA Sentinel', 'CDP', 'BOM Australia'],
    },
    defence: {
      sectorId: 'defence',
      title: 'Defence Intelligence Command',
      subtitle: 'Sovereign Multi-Domain Threat Fusion',
      stats: [
        { label: 'Threat Signals', value: '847K', trend: '+14%', trendUp: true },
        { label: 'Active Conflicts', value: '47', trend: '+2', trendUp: true },
        { label: 'Supply Chain Risk', value: '2,841', trend: '-8%', trendUp: false },
        { label: 'DISP Score', value: '98.7', trend: '+0.3%', trendUp: true },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'CLASSIFIED — Multi-domain threat correlation detected (Indo-Pacific)',
        },
        {
          severity: 'high',
          text: 'Supply chain vulnerability — ITAR-controlled component (Chip Fab #7)',
        },
        {
          severity: 'medium',
          text: 'Cyber-kinetic correlation — Power grid + SIGINT anomaly overlap',
        },
      ],
      chartBars: [78, 82, 76, 89, 94, 87, 91, 85, 92, 96, 88, 94],
      tableRows: [
        {
          col1: 'Indo-Pacific Region',
          col2: 'Threat Level: HIGH',
          col3: 'Naval + Cyber',
          status: 'critical',
        },
        {
          col1: 'Middle East Theatre',
          col2: 'Threat Level: ELEVATED',
          col3: 'Multi-domain',
          status: 'warning',
        },
        {
          col1: 'Five Eyes Network',
          col2: 'Status: ACTIVE',
          col3: 'Intel sharing',
          status: 'active',
        },
        {
          col1: 'Arctic Operations',
          col2: 'Threat Level: MODERATE',
          col3: 'Maritime + Space',
          status: 'active',
        },
      ],
      sidebarItems: [
        'Command Centre',
        'Threat Fusion',
        'Adversary Intel',
        'Supply Chain',
        'Cyber Ops',
        'SIGINT',
        'Reports',
        'Secure Comms',
      ],
      liveFeeds: ['OSINT', 'SIGINT Fusion', 'Satellite ISR', 'NATO STANAG', 'Five Eyes'],
    },
    energy: {
      sectorId: 'energy',
      title: 'Energy Intelligence Grid',
      subtitle: 'Critical Infrastructure & Market Intelligence',
      stats: [
        { label: 'Grid Events', value: '14.2K', trend: '+6.4%', trendUp: true },
        { label: 'Commodity Price', value: '$87.42', trend: '+3.1%', trendUp: true },
        { label: 'Renewable %', value: '34.7%', trend: '+8.2%', trendUp: true },
        { label: 'Risk Alerts', value: '247', trend: '-12%', trendUp: false },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'Grid instability detected — NSW interconnector load exceeding 94%',
        },
        { severity: 'high', text: 'SCADA anomaly — Substation Alpha-7 communications irregular' },
        {
          severity: 'medium',
          text: 'Commodity forecast: Brent crude $92+ projected within 7 days',
        },
      ],
      chartBars: [67, 72, 78, 81, 76, 84, 89, 92, 87, 91, 94, 88],
      tableRows: [
        { col1: 'NSW Grid', col2: 'Load: 94.2%', col3: '12,847 MW', status: 'critical' },
        { col1: 'VIC Grid', col2: 'Load: 67.8%', col3: '8,421 MW', status: 'active' },
        { col1: 'QLD Grid', col2: 'Load: 78.3%', col3: '9,847 MW', status: 'warning' },
        { col1: 'SA Grid', col2: 'Load: 52.1%', col3: '3,241 MW', status: 'active' },
      ],
      sidebarItems: [
        'Dashboard',
        'Grid Monitor',
        'SCADA Intel',
        'Commodities',
        'Renewables',
        'Infrastructure',
        'Reports',
        'API Access',
      ],
      liveFeeds: ['AEMO', 'NERC', 'IEA', 'OPEC', 'BOM Weather'],
    },
    maritime: {
      sectorId: 'maritime',
      title: 'Maritime Intelligence Centre',
      subtitle: 'Global Vessel Tracking & Port Intelligence',
      stats: [
        { label: 'Vessels Tracked', value: '142K', trend: '+4.7%', trendUp: true },
        { label: 'Port Events', value: '8,471', trend: '+12%', trendUp: true },
        { label: 'Sanctions Hits', value: '847', trend: '+28%', trendUp: true },
        { label: 'Route Risk', value: '2.4K', trend: '-6%', trendUp: false },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'AIS dark zone detected — 3 vessels transponders off (Persian Gulf)',
        },
        {
          severity: 'high',
          text: 'Port congestion critical — Singapore Strait 72hr+ delay projected',
        },
        {
          severity: 'medium',
          text: 'Sanctions screening match — Vessel IMO 9847291 (flag change detected)',
        },
      ],
      chartBars: [58, 64, 71, 67, 78, 82, 76, 84, 89, 91, 87, 93],
      tableRows: [
        {
          col1: 'MV Pacific Dawn',
          col2: 'IMO: 9847291',
          col3: 'Singapore Strait',
          status: 'warning',
        },
        { col1: 'MSC Aurora', col2: 'IMO: 9721843', col3: 'Suez Canal', status: 'active' },
        { col1: 'Unknown Vessel', col2: 'AIS: OFF', col3: 'Persian Gulf', status: 'critical' },
        { col1: 'CMA CGM Marco', col2: 'IMO: 9684721', col3: 'Port Melbourne', status: 'active' },
      ],
      sidebarItems: [
        'Dashboard',
        'Vessel Tracking',
        'Port Intel',
        'Sanctions',
        'Route Analysis',
        'Weather',
        'Reports',
        'API Access',
      ],
      liveFeeds: ['MarineTraffic', 'IMO', "Lloyd's List", 'ISPS Reports', 'UN Sanctions'],
    },
    space: {
      sectorId: 'space',
      title: 'Space Domain Awareness',
      subtitle: 'Orbital Intelligence & Spectrum Monitoring',
      stats: [
        { label: 'Objects Tracked', value: '47.8K', trend: '+7.2%', trendUp: true },
        { label: 'Conjunctions', value: '1,247', trend: '+18%', trendUp: true },
        { label: 'Spectrum Events', value: '847', trend: '+12%', trendUp: true },
        { label: 'Launch Events', value: '142', trend: '+34%', trendUp: true },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'Conjunction alert — Starlink-4827 & debris object (T-6hrs, 127m miss)',
        },
        {
          severity: 'high',
          text: 'Spectrum interference detected — X-band overlap, GEO slot 108.2°E',
        },
        {
          severity: 'medium',
          text: 'Launch window update — SpaceX Falcon 9 NET 72hrs (Vandenberg)',
        },
      ],
      chartBars: [45, 52, 58, 64, 71, 78, 82, 87, 91, 94, 89, 96],
      tableRows: [
        { col1: 'Starlink-4827', col2: 'LEO 550km', col3: 'Conjunction: T-6h', status: 'critical' },
        { col1: 'ISS (Zarya)', col2: 'LEO 420km', col3: 'Status: Nominal', status: 'active' },
        { col1: 'AEHF-6', col2: 'GEO 35,786km', col3: 'MILCOMM Active', status: 'active' },
        { col1: 'Debris Object', col2: 'LEO 547km', col3: 'Tracked: NORAD', status: 'warning' },
      ],
      sidebarItems: [
        'Dashboard',
        'Orbital Map',
        'Conjunctions',
        'Spectrum',
        'Launches',
        'Debris',
        'Reports',
        'API Access',
      ],
      liveFeeds: ['US Space Command', 'ESA SSA', 'SpaceTrack', 'ITU', 'NASA ODPO'],
    },
    agriculture: {
      sectorId: 'agriculture',
      title: 'Agriculture Intelligence Platform',
      subtitle: 'Farm-to-Market Autonomous Intelligence',
      stats: [
        { label: 'Crop Regions', value: '4,847', trend: '+8.1%', trendUp: true },
        { label: 'Pest Alerts', value: '1,247', trend: '-14%', trendUp: false },
        { label: 'Yield Forecast', value: '94.2%', trend: '+2.8%', trendUp: true },
        { label: 'Market Signals', value: '12.4K', trend: '+18%', trendUp: true },
      ],
      alerts: [
        {
          severity: 'critical',
          text: 'Locust swarm detected — East Africa, projected spread to Aus NW coast',
        },
        {
          severity: 'high',
          text: 'Wheat rust outbreak — SA & VIC regions, biosecurity alert level 3',
        },
        {
          severity: 'medium',
          text: 'Commodity forecast: Wheat futures $8.40/bu projected (+12% in 14 days)',
        },
      ],
      chartBars: [61, 68, 74, 72, 81, 87, 84, 91, 88, 94, 92, 97],
      tableRows: [
        { col1: 'Wheat (NSW)', col2: 'Yield: 3.2t/ha', col3: 'Health: 94%', status: 'active' },
        { col1: 'Cotton (QLD)', col2: 'Yield: 2.1t/ha', col3: 'Health: 87%', status: 'active' },
        { col1: 'Barley (SA)', col2: 'Yield: 2.8t/ha', col3: 'Health: 62%', status: 'warning' },
        { col1: 'Rice (NSW)', col2: 'Yield: 9.4t/ha', col3: 'Health: 91%', status: 'active' },
      ],
      sidebarItems: [
        'Dashboard',
        'Crop Health',
        'Pest Monitor',
        'Weather',
        'Market Intel',
        'Biosecurity',
        'Reports',
        'API Access',
      ],
      liveFeeds: ['FAO GIEWS', 'ABARES', 'USDA FAS', 'BOM', 'Sentinel-2'],
    },
    education: {
      sectorId: 'education',
      title: 'Education Intelligence Hub',
      subtitle: 'Skills Gap Analysis & Workforce Alignment',
      stats: [
        { label: 'Institutions', value: '47.8K', trend: '+3.4%', trendUp: true },
        { label: 'Skills Mapped', value: '2.4M', trend: '+22%', trendUp: true },
        { label: 'Research Papers', value: '847K', trend: '+14%', trendUp: true },
        { label: 'Workforce Gap', value: '15.2%', trend: '-2.1%', trendUp: false },
      ],
      alerts: [
        {
          severity: 'high',
          text: 'Critical skills gap detected — Cybersecurity workforce deficit 3.4M globally',
        },
        {
          severity: 'medium',
          text: 'Curriculum update recommended — AI/ML modules outdated in 47% of programs',
        },
        { severity: 'low', text: 'OECD PISA update — 2026 assessment framework changes published' },
      ],
      chartBars: [52, 58, 64, 71, 67, 74, 81, 78, 87, 84, 91, 94],
      tableRows: [
        {
          col1: 'University of Melbourne',
          col2: 'STEM Programs: 142',
          col3: 'Skills Match: 87%',
          status: 'active',
        },
        { col1: 'MIT', col2: 'STEM Programs: 284', col3: 'Skills Match: 94%', status: 'active' },
        {
          col1: 'Oxford University',
          col2: 'STEM Programs: 198',
          col3: 'Skills Match: 91%',
          status: 'active',
        },
        { col1: 'ANU', col2: 'STEM Programs: 97', col3: 'Skills Match: 72%', status: 'warning' },
      ],
      sidebarItems: [
        'Dashboard',
        'Skills Analysis',
        'Curriculum AI',
        'Research',
        'Workforce',
        'Institutions',
        'Reports',
        'API Access',
      ],
      liveFeeds: ['OECD', 'UNESCO', 'World Bank', 'LinkedIn Skills', 'Google Trends'],
    },
  };

  return dashboards[config.id] || dashboards['medical'];
}

// ─── Status Color Helper ─────────────────────────────────────────────
function getStatusColor(status: string): string {
  switch (status) {
    case 'critical':
      return 'text-red-400 bg-red-500/20 border-red-500/30';
    case 'high':
      return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
    case 'warning':
      return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
    case 'medium':
      return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
    case 'resolved':
    case 'active':
      return 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30';
    case 'low':
      return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
    default:
      return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
  }
}

function getAlertIcon(severity: string): string {
  switch (severity) {
    case 'critical':
      return '🔴';
    case 'high':
      return '🟠';
    case 'medium':
      return '🟡';
    case 'low':
      return '🔵';
    default:
      return '⚪';
  }
}

// ─── Single Dashboard Screenshot Component ───────────────────────────
const DashboardScreenshot: React.FC<{ config: SectorMarketingConfig; isActive: boolean }> = ({
  config,
  isActive,
}) => {
  const preview = getSectorDashboardPreview(config);
  const maxBar = Math.max(...preview.chartBars);

  return (
    <div
      className={`absolute inset-0 transition-all duration-700 ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}
    >
      {/* App Chrome */}
      <div className="w-full h-full bg-slate-950 rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl flex">
        {/* Sidebar */}
        <div
          className={`w-52 shrink-0 bg-linear-to-b ${config.colorFrom}/10 ${config.colorTo}/5 border-r border-slate-800 flex flex-col`}
        >
          {/* Logo Area */}
          <div className="px-4 py-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 bg-linear-to-br ${config.colorFrom} ${config.colorTo} rounded-lg flex items-center justify-center text-sm`}
              >
                {config.icon}
              </div>
              <div>
                <div className="text-xs font-bold text-white truncate">{config.brandName}</div>
                <div className="text-[9px] text-slate-500">Intelligence Platform</div>
              </div>
            </div>
          </div>
          {/* Nav Items */}
          <div className="flex-1 py-3 px-2 space-y-0.5 overflow-hidden">
            {preview.sidebarItems.map((item, i) => (
              <div
                key={item}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] ${
                  i === 0
                    ? `bg-linear-to-r ${config.colorFrom}/20 ${config.colorTo}/20 ${config.textColor} font-semibold border border-white/5`
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                {item}
              </div>
            ))}
          </div>
          {/* Module Count Badge */}
          <div className="px-3 py-2 border-t border-slate-800">
            <div
              className={`bg-linear-to-r ${config.colorFrom}/15 ${config.colorTo}/15 border ${config.borderColor}/20 rounded-lg p-2 text-center`}
            >
              <div
                className={`text-sm font-black bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
              >
                {getSectorModules(config.id).total}+
              </div>
              <div className="text-[7px] text-slate-500 font-medium">ACTIVE MODULES</div>
            </div>
          </div>
          {/* Sidebar Footer */}
          <div className="px-4 py-2 border-t border-slate-800">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[9px] text-slate-400">
                TD
              </div>
              <div>
                <div className="text-[10px] text-slate-400">Demo User</div>
                <div className="text-[8px] text-slate-600">Enterprise Plan</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Bar */}
          <div className="h-10 border-b border-slate-800 flex items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <h3 className="text-xs font-bold text-white">{preview.title}</h3>
              <span className="text-[9px] text-slate-500">{preview.subtitle}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                LIVE
              </span>
              <span className="text-[9px] text-slate-600">|</span>
              <span className="text-[9px] text-slate-500">SYMPHONIC Engine: Active</span>
            </div>
          </div>

          {/* Content Grid */}
          <div className="flex-1 p-3 overflow-hidden">
            <div className="grid grid-cols-4 gap-2 mb-3">
              {/* Stat Cards */}
              {preview.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-slate-900/80 border border-slate-800 rounded-lg p-2.5"
                >
                  <div className="text-[9px] text-slate-500 mb-0.5">{stat.label}</div>
                  <div
                    className={`text-lg font-bold bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                  >
                    {stat.value}
                  </div>
                  <div
                    className={`text-[9px] ${stat.trendUp ? 'text-emerald-400' : 'text-red-400'}`}
                  >
                    {stat.trend} {stat.trendUp ? '↑' : '↓'}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              {/* Chart Area */}
              <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-3">
                <div className="text-[10px] font-semibold text-white mb-2">
                  Intelligence Activity (12 Months)
                </div>
                <div className="flex items-end gap-1 h-20">
                  {preview.chartBars.map((val, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                      <div
                        className={`w-full bg-linear-to-t ${config.colorFrom} ${config.colorTo} rounded-t-sm opacity-80`}
                        style={{ height: `${(val / maxBar) * 100}%` }}
                      />
                      <span className="text-[7px] text-slate-600">
                        {['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][i]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alerts Panel */}
              <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-3">
                <div className="text-[10px] font-semibold text-white mb-2">Active Alerts</div>
                <div className="space-y-1.5">
                  {preview.alerts.map((alert, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-1.5 p-1.5 rounded border ${getStatusColor(alert.severity)}`}
                    >
                      <span className="text-[10px] shrink-0">{getAlertIcon(alert.severity)}</span>
                      <span className="text-[8px] leading-tight">{alert.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Data Table */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-semibold text-white">Live Intelligence Feed</div>
                <div className="flex gap-1">
                  {preview.liveFeeds.slice(0, 3).map((feed) => (
                    <span
                      key={feed}
                      className="text-[7px] px-1.5 py-0.5 bg-slate-800 rounded text-slate-500 border border-slate-700"
                    >
                      {feed}
                    </span>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                {preview.tableRows.map((row, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-1 px-2 bg-slate-800/50 rounded text-[9px]"
                  >
                    <span className="text-white font-medium w-1/3 truncate">{row.col1}</span>
                    <span className="text-slate-400 w-1/3 text-center truncate">{row.col2}</span>
                    <span className="text-slate-400 w-1/6 text-center truncate">{row.col3}</span>
                    <span
                      className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border ${getStatusColor(row.status)}`}
                    >
                      {row.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WATERMARK OVERLAY */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center rounded-2xl overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-br from-transparent via-transparent to-black/20" />
        {/* Diagonal Watermark */}
        <div className="absolute inset-0 flex items-center justify-center -rotate-12">
          <div className="text-center">
            <div
              className={`text-5xl md:text-7xl font-black bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent opacity-[0.08] select-none`}
            >
              {config.brandName.toUpperCase()}
            </div>
            <div className="text-lg md:text-2xl font-bold text-white/6 select-none tracking-widest mt-1">
              PREVIEW — {config.companyStructure.entity.toUpperCase()}
            </div>
          </div>
        </div>
        {/* Corner Badge */}
        <div className="absolute top-3 right-3">
          <div
            className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} px-3 py-1 rounded-full text-[9px] font-bold text-white/90 shadow-lg`}
          >
            {config.icon} LIVE PREVIEW
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Showcase Component ─────────────────────────────────────────
interface SectorScreenshotShowcaseProps {
  autoPlayInterval?: number;
  initialSector?: string;
}

const SectorScreenshotShowcase: React.FC<SectorScreenshotShowcaseProps> = ({
  autoPlayInterval = 6000,
  initialSector,
}) => {
  const sectors = SECTOR_MARKETING_DATA;
  const initialIndex = initialSector ? sectors.findIndex((s) => s.id === initialSector) : 0;
  const [activeIndex, setActiveIndex] = useState(Math.max(0, initialIndex));
  const [isPaused, setIsPaused] = useState(false);

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % sectors.length);
  }, [sectors.length]);

  const goToPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + sectors.length) % sectors.length);
  }, [sectors.length]);

  // Auto-play
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(timer);
  }, [isPaused, autoPlayInterval, goToNext]);

  const activeConfig = sectors[activeIndex];

  return (
    <section className="py-24 px-4 bg-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-linear-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 border border-cyan-500/20 rounded-full px-6 py-2 mb-6">
            <span className="text-cyan-400 font-bold text-sm tracking-wider">
              🖥️ INSIDE THE PLATFORM — LIVE DASHBOARD PREVIEWS
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-4">
            <span className="bg-linear-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              See What&apos;s Inside
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            42 sovereign intelligence platforms. 1,200+ autonomous modules. Each with real-time
            dashboards, AI-powered alerts, self-auditing, self-repair, and evolution engines.
            Here&apos;s what&apos;s inside.
          </p>
          {/* Platform-wide module stats */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {[
              { label: 'Total Modules', value: '1,200+', icon: '🧩' },
              { label: 'React Components', value: '239', icon: '⚛️' },
              { label: 'Database Models', value: '128', icon: '🗄️' },
              { label: 'API Endpoints', value: '438', icon: '🔌' },
              { label: 'AI Engines', value: '28', icon: '🤖' },
              { label: 'Evolution Engines', value: '22', icon: '🧬' },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-slate-800/50 border border-slate-700/30 rounded-xl px-4 py-2 text-center"
              >
                <div className="text-lg">{s.icon}</div>
                <div className="text-lg font-black bg-linear-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  {s.value}
                </div>
                <div className="text-[9px] text-slate-500 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sector Selector Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {sectors.map((sector, i) => (
            <button
              key={sector.id}
              onClick={() => {
                setActiveIndex(i);
                setIsPaused(true);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                i === activeIndex
                  ? `bg-linear-to-r ${sector.colorFrom} ${sector.colorTo} text-white shadow-lg scale-105`
                  : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700/60 hover:text-white border border-slate-700/30'
              }`}
            >
              <span>{sector.icon}</span>
              <span className="hidden sm:inline">{sector.brandName.replace('Anchor ', '')}</span>
            </button>
          ))}
        </div>

        {/* Screenshot Container */}
        <div
          className="relative w-full aspect-16/10 max-h-150 mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* All Dashboard Screenshots (stacked) */}
          {sectors.map((sector, i) => (
            <DashboardScreenshot key={sector.id} config={sector} isActive={i === activeIndex} />
          ))}

          {/* Navigation Arrows */}
          <button
            onClick={() => {
              goToPrev();
              setIsPaused(true);
            }}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-10 h-10 bg-slate-800/90 border border-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-700 transition-colors shadow-xl"
            aria-label="Previous sector"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={() => {
              goToNext();
              setIsPaused(true);
            }}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-10 h-10 bg-slate-800/90 border border-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-700 transition-colors shadow-xl"
            aria-label="Next sector"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Sector Info Strip */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div
              className={`w-10 h-10 bg-linear-to-br ${activeConfig.colorFrom} ${activeConfig.colorTo} rounded-xl flex items-center justify-center text-xl shadow-lg`}
            >
              {activeConfig.icon}
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{activeConfig.brandName}</h3>
              <p className={`text-xs ${activeConfig.textColor}`}>
                {activeConfig.companyStructure.entity}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-400 max-w-2xl mx-auto mb-4">
            {activeConfig.solutionStatement}
          </p>

          {/* Module Categories Grid */}
          <div className="grid grid-cols-5 md:grid-cols-10 gap-1.5 max-w-4xl mx-auto mb-4">
            {getSectorModules(activeConfig.id).categories.map((mod) => (
              <div
                key={mod.name}
                className={`bg-slate-800/60 border border-slate-700/30 rounded-lg p-1.5 text-center hover:border-white/20 transition-colors group`}
              >
                <div className="text-sm group-hover:scale-110 transition-transform">{mod.icon}</div>
                <div
                  className={`text-[9px] font-bold bg-linear-to-r ${activeConfig.colorFrom} ${activeConfig.colorTo} bg-clip-text text-transparent`}
                >
                  {mod.count}
                </div>
                <div className="text-[7px] text-slate-500 leading-tight truncate" title={mod.name}>
                  {mod.name}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-2 mb-3">
            <span
              className={`text-xs font-bold bg-linear-to-r ${activeConfig.colorFrom} ${activeConfig.colorTo} bg-clip-text text-transparent`}
            >
              {getSectorModules(activeConfig.id).total} modules
            </span>
            <span className="text-[10px] text-slate-600">
              across {getSectorModules(activeConfig.id).categories.length} categories
            </span>
            <span className="text-[10px] text-slate-700">|</span>
            <span className="text-[10px] text-slate-500">
              Self-auditing • Self-repairing • Autonomous evolution
            </span>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {activeConfig.features
              .filter((f) => f.worldFirst)
              .slice(0, 4)
              .map((f) => (
                <span
                  key={f.title}
                  className={`text-[10px] px-2.5 py-1 bg-linear-to-r ${activeConfig.colorFrom}/10 ${activeConfig.colorTo}/10 border ${activeConfig.borderColor}/20 rounded-full ${activeConfig.textColor}`}
                >
                  🏆 {f.title}
                </span>
              ))}
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {sectors.map((sector, i) => (
            <button
              key={sector.id}
              onClick={() => {
                setActiveIndex(i);
                setIsPaused(true);
              }}
              className={`transition-all duration-300 rounded-full ${
                i === activeIndex
                  ? `w-8 h-2 bg-linear-to-r ${sector.colorFrom} ${sector.colorTo}`
                  : 'w-2 h-2 bg-slate-700 hover:bg-slate-500'
              }`}
              aria-label={sector.brandName}
            />
          ))}
        </div>

        {/* Auto-play indicator */}
        <div className="text-center mt-4">
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors"
          >
            {isPaused ? '▶ Resume auto-play' : '⏸ Auto-playing — hover or click to pause'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default SectorScreenshotShowcase;
